import { useEffect, useRef, useState } from 'react';

import { Icon } from './Icon';
import './PresentationBadge.css';

/**
 * Значок работающего режима презентации.
 *
 * Виден всем: участник должен понимать, почему раскладка выглядит не так,
 * как обычно, — иначе это читается как сбой. По нажатию раскрывается
 * короткое объяснение, что режим делает и кто его выключит.
 */
export function PresentationBadge({ isHost, onDisable }: { isHost: boolean; onDisable: () => void }) {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  // Закрытие по клику мимо и по Escape — как у панели качества.
  useEffect(() => {
    if (!open) return;

    const onPointer = (event: PointerEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="pbadge" ref={hostRef}>
      <button
        type="button"
        className={`pbadge__button ${open ? 'pbadge__button--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Что такое режим презентации"
      >
        <Icon name="present" size={15} />
        <span className="pbadge__text">Презентация</span>
        <span className="pbadge__mark" aria-hidden>
          ?
        </span>
      </button>

      {open && (
        <div className="pbadge__pop" role="dialog" aria-label="Режим презентации">
          <h3 className="pbadge__title">Режим презентации</h3>
          <p className="pbadge__line">
            Показываемое занимает весь экран — лента с лицами внизу убрана, чтобы не
            съедать место под слайдом.
          </p>
          <p className="pbadge__line">
            Докладчик появляется маленьким окном в углу, когда говорит, и уходит в
            паузах. Запись встречи ведётся в этой же раскладке.
          </p>
          <p className="pbadge__line pbadge__line--dim">
            Кто подключится позже, сразу попадёт в этот режим. Включает и выключает его
            только организатор.
          </p>

          {isHost && (
            <button
              type="button"
              className="btn btn--ghost btn--sm pbadge__off"
              onClick={() => {
                setOpen(false);
                onDisable();
              }}
            >
              Выключить режим
            </button>
          )}
        </div>
      )}
    </div>
  );
}
